import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as _ from 'lodash';
import { EmptyList } from '../../../shared/empty-list/EmptyList';
import { SpinLoader } from '../../../shared/spin-loader/SpinLoader';
import FriendlyDate from '../../../shared/FriendlyDate';
import { TimeZoneNote } from '../../../shared/time-zone-note/TimeZoneNote';
import { Pager } from '../../../shared/pager/Pager';
import { Sorter } from '../../../shared/table/Sorter';
import { LabeledSelect } from '../../../shared/labeled-select/LabeledSelect';
import { BlueContainedButton } from '../../../shared/button/blue-contained-button/BlueContainedButton';
import { isSandboxJurisdiction } from '../../../shared/models/jurisdictions';
import { humanize } from '../../../shared/helpers/text.helper';
import { fetchAuditEvents, fetchAuditUsers } from './redux/actions';
import { getAuditState, getAuditUsers } from './redux/selectors';
import { getBrandsAndJurisdictions } from '../../../jurisdiction/redux/selectors';
import '../../../shared/table/AlertsTable.scss';
import './audit.scss';

/* sortKey: what the API sorts by, for the columns that it can sort by */
const headers = [
    { key: 'TIME', value: 'When', sortKey: 'EVENT_TIMESTAMP' },
    { key: 'EVENT', value: 'Event', sortKey: 'EVENT_TYPE' },
    { key: 'DESCRIPTION', value: 'What happened' },
    { key: 'ENTITY', value: 'About' },
    { key: 'JURISDICTION', value: 'Jurisdiction' },
    { key: 'USER', value: 'By' }
];

/* Events about nobody in particular, such as those that the system makes itself, have the id 0 for an author. */
const SYSTEM_USER_ID = '0';

const ALL_REAL = '';

/* The jurisdictions to look at the events of. A sandbox is looked at by naming it: its events are not among the others. */
export const makeJurisdictionOptions = jurisdictions => [
    { value: ALL_REAL, label: 'All except sandboxes' },
    ..._.map(
        _.sortBy(jurisdictions, [j => isSandboxJurisdiction(j), 'code']),
        jurisdiction => ({
            value: jurisdiction.code,
            label: isSandboxJurisdiction(jurisdiction)
                ? `${jurisdiction.name} · ${jurisdiction.code}`
                : `${jurisdiction.name} (${jurisdiction.code})`
        })
    )
];

/* The audit trail: who did what, and when, to the rules, the access and the sandboxes. Newest first. */
class AuditContainer extends Component {
    constructor(props) {
        super(props);
        // What is typed in for the type of event, until it is searched for
        this.state = { eventType: props.eventType || '' };
    }

    componentDidMount() {
        const { jurisdiction, eventType, pagination, sort } = this.props;

        // To say who did it by name, from the admins that anyone with the ADMIN role may read (not the list of users, which is for those who manage them)
        this.props.fetchAuditUsers();

        // A link may name what to look at (a sandbox), and otherwise what was looked at last stays
        this.props.fetchAuditEvents({
            page: 1,
            pageSize: pagination.pageSize,
            ...sort,
            jurisdiction:
                this.props.initialJurisdiction === undefined
                    ? jurisdiction
                    : this.props.initialJurisdiction,
            eventType
        });
    }

    search = changes =>
        this.props.fetchAuditEvents({
            page: 1,
            pageSize: this.props.pagination.pageSize,
            ...this.props.sort,
            jurisdiction: this.props.jurisdiction,
            eventType: this.props.eventType,
            ...changes
        });

    updatePagination = ({ page, pageSize }) =>
        this.props.fetchAuditEvents({
            page,
            pageSize,
            ...this.props.sort,
            jurisdiction: this.props.jurisdiction,
            eventType: this.props.eventType
        });

    updateSort = ({ sortingKey, sortDirection }) =>
        this.search({
            sortType: _.get(_.find(headers, { key: sortingKey }), 'sortKey'),
            sortOrder: sortDirection
        });

    /* By name when the user is known, and by the id when not: someone who is gone, or who may not be read. */
    describeUser = userId => {
        const user = this.props.usersById[String(userId)];

        return String(userId) === SYSTEM_USER_ID ? (
            'System'
        ) : user ? (
            `${user.firstName} ${user.lastName}`
        ) : (
            <span className={'audit-muted audit-mono'}>{userId}</span>
        );
    };

    renderRow = event => (
        <tr key={event.eventId} className="data">
            <td>
                <FriendlyDate timestamp={event.eventTimestamp} />
            </td>
            <td>
                <div>{humanize(event.eventType)}</div>
                {event.tenantType === 'SIMULATION' && (
                    <span className={'audit-tag'}>sandbox</span>
                )}
            </td>
            <td>{event.description}</td>
            <td>
                {event.entityType ? (
                    <div>
                        <div>{humanize(event.entityType)}</div>
                        {event.entityId && (
                            <div className={'audit-muted audit-mono'}>
                                #{event.entityId}
                            </div>
                        )}
                    </div>
                ) : (
                    <span className={'audit-muted'}>-</span>
                )}
            </td>
            <td>
                {event.jurisdiction || <span className={'audit-muted'}>-</span>}
            </td>
            <td>{this.describeUser(event.userId)}</td>
        </tr>
    );

    render() {
        const {
            events,
            loaded,
            loading,
            pagination,
            sort,
            jurisdiction,
            jurisdictions
        } = this.props;

        const jurisdictionOptions = makeJurisdictionOptions(jurisdictions);

        return (
            <div className={'container audit'}>
                <div className={'audit-filters sim-form'}>
                    <LabeledSelect
                        className={'audit-filter'}
                        label={'Events about'}
                        options={jurisdictionOptions}
                        value={_.find(jurisdictionOptions, {
                            value: jurisdiction
                        })}
                        onChange={value => this.search({ jurisdiction: value })}
                    />
                    <div className={'audit-filter'}>
                        <span className={'input-label'}>Type of event</span>
                        <input
                            type={'text'}
                            value={this.state.eventType}
                            placeholder={'For example SIM_WS_DELETED'}
                            onChange={event =>
                                this.setState({ eventType: event.target.value })
                            }
                            onKeyDown={event =>
                                event.key === 'Enter' &&
                                this.search({
                                    eventType: _.trim(this.state.eventType)
                                })
                            }
                        />
                    </div>
                    <div className={'audit-filter audit-search'}>
                        <BlueContainedButton
                            text={'Search'}
                            onClick={() =>
                                this.search({
                                    eventType: _.trim(this.state.eventType)
                                })
                            }
                        />
                    </div>
                </div>

                <div className={'table'}>
                    <TimeZoneNote />
                    <table>
                        <tbody>
                            <tr className="headers">
                                {headers.map(header => (
                                    <th key={header.key}>
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center'
                                            }}
                                        >
                                            <span>{header.value}</span>
                                            {header.sortKey && (
                                                <Sorter
                                                    sortingKey={header.key}
                                                    currentSortingKey={
                                                        (
                                                            _.find(headers, {
                                                                sortKey:
                                                                    sort.sortType
                                                            }) || {}
                                                        ).key
                                                    }
                                                    onSort={this.updateSort}
                                                />
                                            )}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                            {loading && !loaded ? (
                                <tr>
                                    <td colSpan={headers.length}>
                                        <SpinLoader />
                                    </td>
                                </tr>
                            ) : _.isEmpty(events) ? (
                                <tr className="data">
                                    <td
                                        colSpan={headers.length}
                                        style={{ textAlign: 'center' }}
                                    >
                                        <EmptyList />
                                    </td>
                                </tr>
                            ) : (
                                events.map(this.renderRow)
                            )}
                            <Pager
                                pagination={pagination}
                                onPaginationChanged={this.updatePagination}
                                colspan={headers.length}
                            />
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state, ownProps) => {
    const { data, loaded, loading, pagination, sort, filters } = getAuditState(
        state
    );
    // The address may name a sandbox to look at: /audit?jurisdiction=XM
    const named = new URLSearchParams(
        _.get(ownProps, 'location.search', '')
    ).get('jurisdiction');

    return {
        events: data,
        loaded,
        loading,
        pagination,
        sort,
        jurisdiction: filters.jurisdiction,
        eventType: filters.eventType,
        initialJurisdiction: named === null ? undefined : named,
        jurisdictions: _.get(
            getBrandsAndJurisdictions(state),
            'jurisdictions',
            []
        ),
        usersById: _.keyBy(getAuditUsers(state), user => String(user.id))
    };
};

const mapDispatchToProps = dispatch => ({
    fetchAuditEvents: args => dispatch(fetchAuditEvents(args)),
    fetchAuditUsers: () => dispatch(fetchAuditUsers())
});

export default connect(mapStateToProps, mapDispatchToProps)(AuditContainer);
